import { useCallback, useEffect, useRef, useState } from 'react';
import Nav, { type AppTab } from './components/Nav';
import ChatWindow from './components/ChatWindow';
import DraftsPanel from './components/DraftsPanel';
import Footer from './components/Footer';
import DiscoverFeed from './components/DiscoverFeed';
import { AuthProvider, useAuth } from './hooks/useAuth';

function AppInner() {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<AppTab>('discover');
  const [draftsOpen, setDraftsOpen] = useState(false);
  const hydrateFnRef = useRef<((threadId: number) => Promise<void>) | null>(null);
  const resetFnRef = useRef<(() => void) | null>(null);
  const prefillRef = useRef<((text: string) => void) | null>(null);

  // ── Dynamically measure footer height so the scroll area isn't hidden ──
  const footerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight;
      document.documentElement.style.setProperty('--footer-h', `${h}px`);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Search state (lifted so Nav renders the bar, DiscoverFeed uses it)
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  const handleRequestHydration = useCallback(
    (fn: (threadId: number) => Promise<void>) => {
      hydrateFnRef.current = fn;
    },
    []
  );

  const handleRequestReset = useCallback((fn: () => void) => {
    resetFnRef.current = fn;
  }, []);

  const handleRequestPrefill = useCallback((fn: (text: string) => void) => {
    prefillRef.current = fn;
  }, []);

  const handlePickDraft = useCallback(async (threadId: number) => {
    setActiveTab('profile');
    if (hydrateFnRef.current) {
      await hydrateFnRef.current(threadId);
    }
  }, []);

  const handleSignOutFromDrawer = useCallback(async () => {
    await logout();
    setActiveTab('discover');
  }, [logout]);

  /** When the user sends a message from the always-on chat bar,
   *  flip to the profile/chat view so they see the stream. */
  const handleChatSend = useCallback(() => {
    setActiveTab('profile');
  }, []);

  // Research-chat context: when set, ChatWindow enters "ask this research" mode
  const [researchContext, setResearchContext] = useState<{
    threadId: number;
    title: string;
  } | null>(null);

  /** Called when user clicks "ask this research" in the article reader.
   *  Closes the article, switches to profile tab, and activates research-chat mode. */
  const handleAskResearch = useCallback(
    (threadId: number, title: string) => {
      setResearchContext({ threadId, title });
      setActiveTab('profile');
    },
    []
  );

  const handleClearResearchContext = useCallback(() => {
    setResearchContext(null);
  }, []);

  /** Called when user clicks "start new thread from this insight" in the
   *  article reader. Resets the chat, prefills a question, and switches
   *  to the profile tab. */
  const handleStartThreadFromInsight = useCallback(
    (sourceTitle: string, _sourceThreadId: number) => {
      setResearchContext(null);
      if (resetFnRef.current) resetFnRef.current();
      setActiveTab('profile');
      // Small delay so ChatWindow mounts / resets before we prefill
      requestAnimationFrame(() => {
        if (prefillRef.current) {
          prefillRef.current(
            `I just read "${sourceTitle}" — I'd like to explore this topic further. What are the key takeaways and what should I dig into next?`
          );
        }
      });
    },
    []
  );

  return (
    <div className="app-shell">
      <Nav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onOpenDrafts={() => setDraftsOpen((v) => !v)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searching={searching}
      />
      <DraftsPanel
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        onPick={handlePickDraft}
        onSignOut={handleSignOutFromDrawer}
      />
      <main className="app-main">
        <div className="app-col app-col--flex">
          {/* Discover feed — always mounted, hidden when profile is active */}
          <div style={activeTab === 'discover' ? undefined : { display: 'none' }}>
            <DiscoverFeed
              onStartThread={handleStartThreadFromInsight}
              onAskResearch={handleAskResearch}
              searchQuery={searchQuery}
              onSearchingChange={setSearching}
            />
          </div>
          {/* ChatWindow is always mounted so input bar stays visible.
              Stream area is hidden when discover is the active tab. */}
          <ChatWindow
            draftsOpen={draftsOpen}
            onCloseDrafts={() => setDraftsOpen(false)}
            onRequestHydration={handleRequestHydration}
            onRequestReset={handleRequestReset}
            onRequestPrefill={handleRequestPrefill}
            streamVisible={activeTab === 'profile'}
            onSendTriggered={handleChatSend}
            researchContext={researchContext}
            onClearResearchContext={handleClearResearchContext}
          />
        </div>
      </main>
      <footer className="app-footer" ref={footerRef}>
        <div className="app-col" id="app-footer-portal" />
        <Footer />
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
